import {
  VStack, HStack, Flex, Divider, Box, SkeletonText, Text,
} from "@chakra-ui/react";
import MeetingLinksFields from "features/Resources/Meeting/Components/MeetingLinksFields";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import { longAttributeStyles } from "components/DataDisplay/styles";
import { Text14 } from "components/Text";
import { ShowAttendees } from "features/Resources/Meeting/Components/DisplayAttendees";
import { AdminMeetingTypeTag } from "design-system";
import { AttendeeNameTag } from "components/Tag/ResourceTargetTag/MeetingAttendeeTag";
import { ShowRichText } from "components/FormElements/Fields/RichTextAreaField";
import ScheduledAtTag from "features/Resources/Meeting/Components/ScheduledAtTag";
import InfoOutlineRounded from "components/Icon/Iconify/InfoOutlineRounded";
import LinkButton from "components/Button/ExternalLinkButton";
import { MeetingEvent } from "../models";

interface MeetingEventDetailsProps {
  meetingEvent: MeetingEvent;
  showHeader?: boolean;
  isLoading?: boolean;
}

export default function MeetingEventDetails({
  meetingEvent,
  showHeader = true,
  isLoading = false,
}: MeetingEventDetailsProps) {
  return (
    <VStack spacing={2} align="stretch" width="100%">
      {showHeader && (
        <>
          <VStack align="start" spacing="3">
            <HStack spacing="3">
              <AdminMeetingTypeTag.IconBadge meetingType={meetingEvent.meetingType} />
              <Flex align="center" gap="2">
                <Text14 fontWeight="bold" fontSize="lg">
                  {meetingEvent.title}
                </Text14>
              </Flex>
            </HStack>
            <ScheduledAtTag
              scheduledAt={meetingEvent.startTime}
              timezone={meetingEvent.timezone}
              durationMinutes={meetingEvent.durationMinutes}
              status={meetingEvent.status}
              width="100%"
            />

          </VStack>
          <Divider my="2" />
        </>
      )}

      <ShowAttendees
        label="Attendees"
        attendees={meetingEvent.leader ? meetingEvent.attendees.filter((a) => a.userId !== meetingEvent.leader?.id) : meetingEvent.attendees}
        leadId={meetingEvent.leader?.id}
      />

      {meetingEvent.leader && (
        <HorizontalGreyBlackField
          label="Lead"
          labelProps={{ marginBottom: 0 }}
          labelContainerProps={longAttributeStyles.labelContainerProps}
        >
          <AttendeeNameTag
            name={meetingEvent.leader.fullName}
            responseStatus={meetingEvent.attendees.find((a) => a.userId === meetingEvent.leader?.id)?.responseStatus}
          />
        </HorizontalGreyBlackField>
      )}

      <MeetingLinksFields
        videoConferenceUrl={meetingEvent.videoConferenceUrl}
        videoConferenceProvider={meetingEvent.videoConferenceProvider}
        externalUrl={meetingEvent.externalUrl}
      />

      <Box my="1" />

      {
        [
          [meetingEvent.purposeDisplay, "Purpose"],
          [meetingEvent.learningObjectiveDisplay, "Learning Objective"],
          [meetingEvent.additionalInfoDisplay, "Additional Info"],
        ].map(([richText, label]) => richText && label && <ShowRichText key={`rich-text-${label}`} label={label} richText={richText} />)
      }

      {meetingEvent.externalUrl && (
        <VStack mt="4" spacing="3" align="stretch">
          <Box
            p="5"
            bg="cdsIndigo.50"
            borderWidth="1px"
            borderColor="cdsIndigo.600"
            borderRadius="md"
          >
            <VStack align="start" spacing="1">
              <InfoOutlineRounded boxSize="6" color="cdsIndigo.600" />
              <Text fontWeight="semibold" color="cdsIndigo.600">
                Need to make changes to this meeting?
              </Text>
              <Text fontSize="sm" color="cdsGrey.800">
                All meeting details are synced with Google Calendar. To edit participants, time, or any other details, please use the button below to open this event in Google Calendar.
              </Text>
            </VStack>
          </Box>
          <LinkButton href={meetingEvent.externalUrl} width="100%">
            Edit in Google Calendar
          </LinkButton>
        </VStack>
      )}

      {isLoading && (
        <SkeletonText noOfLines={5} />
      )}
    </VStack>
  );
}
